import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import {
  getReceipts,
  getReceiptById,
  exportCustomerReceiptsSummaryToExcel,
  exportCustomerReceiptsDetailsToExcel
} from "../../components/Api/GmsReceipt";
import { Eye, FileText, Download } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  hasPagePermissionWithAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { formatDateForAPI, formatDateForDisplay } from "../../helpers/dateHelper";
import API_URL from "../../config";

const emptyFilter = {
  ReceiptId: null,
  CustomerId: null,
  BusinessId: null,
  BranchId: null,
  FromDate: null,
  ToDate: null,
  PaymentType: null,
};

export default function CustomerReceiptsPage() {
  const [filter, setFilter] = useState(emptyFilter);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptDetails, setReceiptDetails] = useState(null);
  const [loadingReceiptDetails, setLoadingReceiptDetails] = useState(false);
  const [exportingSummary, setExportingSummary] = useState(false);
  const [exportingReceiptId, setExportingReceiptId] = useState(null);

  // Permission states
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsCustomerReceipts');

  useEffect(() => {
    if (hasPermission) {
      loadReceipts();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);


  const loadReceipts = async () => {
    try {
      setLoading(true);
      // Build filter data, excluding null values
      const filterData = {
        Type: "cu",
        Page: page,
        PageSize: pageSize,
      };

      // Only add non-null filter values
      if (filter.ReceiptId) filterData.ReceiptId = filter.ReceiptId;
      if (filter.CustomerId) filterData.CustomerId = filter.CustomerId;
      if (filter.BusinessId) filterData.BusinessId = filter.BusinessId;
      if (filter.BranchId) filterData.BranchId = filter.BranchId;
      if (filter.FromDate) filterData.FromDate = formatDateForAPI(filter.FromDate, false);
      if (filter.ToDate) filterData.ToDate = formatDateForAPI(filter.ToDate, false);
      if (filter.PaymentType) filterData.PaymentType = filter.PaymentType;

      const response = await getReceipts(filterData);
      setReceipts(response.receipts || []);
      setTotalCount(response.totalCount || 0);
    } catch (error) {
      console.error("Error loading receipts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (receipt) => {
    try {
      // Handle both camelCase and PascalCase from API
      const receiptId = receipt.receiptId || receipt.ReceiptId;

      setViewLoadingRow(receiptId);
      setLoadingReceiptDetails(true);
      setShowReceiptModal(true);

      // Use receipt data from list (has customer/business/branch names) and fetch details
      const receiptData = await getReceiptById(receiptId);

      console.log("Receipt from list:", receipt);
      console.log("Receipt details from API:", receiptData);

      // Merge receipt data from list (has names) with details from API
      const mergedReceipt = {
        ...receipt, // This has customer_NameEn, business_NameEn, branch_NameEn
        ...receiptData, // This has ReceiptDetails
        // Override with API data if available, but keep names from list
        receiptDetails: receiptData?.receiptDetails || receiptData?.ReceiptDetails || []
      };

      console.log("Merged receipt data:", mergedReceipt);
      console.log("Receipt details array:", mergedReceipt.receiptDetails);

      setReceiptDetails(mergedReceipt);
    } catch (error) {
      console.error("Error viewing receipt:", error);
      Swal.fire("Error", "Failed to load receipt details", "error");
      setShowReceiptModal(false);
    } finally {
      setViewLoadingRow(null);
      setLoadingReceiptDetails(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Export handlers
  const handleExportSummary = async () => {
    try {
      setExportingSummary(true);
      // Build filter data for export
      const exportFilter = {
        Type: "cu",
      };

      if (filter.ReceiptId) exportFilter.ReceiptId = filter.ReceiptId;
      if (filter.CustomerId) exportFilter.CustomerId = filter.CustomerId;
      if (filter.BusinessId) exportFilter.BusinessId = filter.BusinessId;
      if (filter.BranchId) exportFilter.BranchId = filter.BranchId;
      if (filter.FromDate) exportFilter.FromDate = formatDateForAPI(filter.FromDate, false);
      if (filter.ToDate) exportFilter.ToDate = formatDateForAPI(filter.ToDate, false);
      if (filter.PaymentType) exportFilter.PaymentType = filter.PaymentType;

      await exportCustomerReceiptsSummaryToExcel(exportFilter);
      Swal.fire("Success", "Customer receipts summary exported successfully", "success");
    } catch (error) {
      console.error("Error exporting customer receipts summary:", error);
      Swal.fire("Error", "Failed to export customer receipts summary", "error");
    } finally {
      setExportingSummary(false);
    }
  };

  const handleExportDetails = async (receipt) => {
    try {
      const receiptId = receipt.receiptId || receipt.ReceiptId;
      setExportingReceiptId(receiptId);

      // First check if receipt has details
      const receiptData = await getReceiptById(receiptId);
      const hasDetails = receiptData?.receiptDetails && receiptData.receiptDetails.length > 0;

      if (!hasDetails) {
        Swal.fire("Info", "This receipt has no payment allocation details to export", "info");
        return;
      }

      // Create filter for this specific receipt
      const exportFilter = {
        Type: "cu",
        ReceiptId: receiptId,
        FromDate: null,
        ToDate: null,
        CustomerId: null,
        BusinessId: null,
        BranchId: null,
        PaymentType: null
      };

      await exportCustomerReceiptsDetailsToExcel(exportFilter);
      Swal.fire("Success", "Customer receipt details exported successfully", "success");
    } catch (error) {
      console.error("Error exporting customer receipt details:", error);
      Swal.fire("Error", "Failed to export customer receipt details", "error");
    } finally {
      setExportingReceiptId(null);
    }
  };

  useEffect(() => {
    loadReceipts();
  }, [page, pageSize, filter]);


  const formatDate = (date) => {
    return formatDateForDisplay(date, 'date');
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Receipts List" breadcrumbItem="Receipts List" />

          <Row>
            <Col xs={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">Receipts List ({totalCount} records)</h5>
                    <div className="d-flex gap-2">
                      <Button
                        color="success"
                        size="sm"
                        onClick={handleExportSummary}
                        disabled={exportingSummary}
                        className="waves-effect waves-light"
                      >
                        {exportingSummary ? (
                          <>
                            <ClipLoader size={14} color="#fff" className="me-1" />
                            Exporting...
                          </>
                        ) : (
                          <>
                            <Download size={14} className="me-1" />
                            Export Summary
                          </>
                        )}
                      </Button>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => setShowFilters(!showFilters)}
                      >
                        {showFilters ? "Hide Filters" : "Show Filters"}
                      </Button>
                    </div>
                  </div>

                  {showFilters && (
                    <Row className="mb-3">
                      <Col md={3}>
                        <FormGroup>
                          <Label>Customer</Label>
                          <Input
                            type="text"
                            placeholder="Search customer..."
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>From Date</Label>
                          <DatePicker
                            selected={filter.FromDate}
                            onChange={(date) => setFilter(prev => ({ ...prev, FromDate: date }))}
                            dateFormat="dd/MM/yyyy"
                            className="form-control"
                            isClearable
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>To Date</Label>
                          <DatePicker
                            selected={filter.ToDate}
                            onChange={(date) => setFilter(prev => ({ ...prev, ToDate: date }))}
                            dateFormat="dd/MM/yyyy"
                            className="form-control"
                            isClearable
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>&nbsp;</Label>
                          <div>
                            <Button color="primary" size="sm" onClick={loadReceipts}>Apply</Button>
                            <Button color="secondary" size="sm" className="ms-2" onClick={() => {
                              setFilter(emptyFilter);
                              setPage(1);
                              loadReceipts();
                            }}>Clear</Button>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  )}

                  {loading ? (
                    <div className="text-center py-4">
                      <ClipLoader color="#556ee6" loading={loading} size={50} />
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table bordered>
                          <thead className="table-light">
                            <tr>
                              <th>Sr.No</th>
                              <th>Receipt Number</th>
                              <th>Date</th>
                              <th>Customer</th>
                              <th>Amount</th>
                              <th>Payment Method</th>
                              <th>Document</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {receipts.length === 0 ? (
                              <tr>
                                <td colSpan="8" className="text-center">No receipts found</td>
                              </tr>
                            ) : (
                              receipts.map((receipt, index) => {
                                // Handle both camelCase and PascalCase from API
                                const receiptId = receipt.receiptId || receipt.ReceiptId;
                                const receiptNumber = receipt.receiptNumber || receipt.ReceiptNumber || 'N/A';
                                const receiptDate = receipt.receiptDate || receipt.ReceiptDate;
                                const amount = receipt.amount || receipt.Amount || 0;
                                const paymentType = receipt.paymentType || receipt.PaymentType || 'ca';
                                // Handle both camelCase and PascalCase for customer name
                                const customerName = receipt.customer_NameEn || receipt.Customer_NameEn || receipt.customerNameEn || 'N/A';
                                const documentPath = receipt.receipt_DocumentPath || receipt.Receipt_DocumentPath || null;

                                return (
                                  <tr key={receiptId}>
                                    <td>{(page - 1) * pageSize + index + 1}</td>
                                    <td>{receiptNumber}</td>
                                    <td>{formatDate(receiptDate)}</td>
                                    <td>{customerName}</td>
                                    <td>{formatCurrency(amount)}</td>
                                    <td>
                                      {paymentType === 'ca' ? 'Cash' :
                                        paymentType === 'cc' ? 'Credit Card' : 'Online'}
                                    </td>
                                    <td>
                                      {documentPath ? (
                                        <a
                                          href={`${API_URL}${documentPath}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="btn btn-sm btn-outline-primary"
                                          title="View Document"
                                        >
                                          <FileText size={16} />
                                        </a>
                                      ) : (
                                        <span className="text-muted">-</span>
                                      )}
                                    </td>
                                    <td>
                                      <div className="d-flex gap-1">
                                        <Button
                                          color="primary"
                                          size="sm"
                                          onClick={() => handleView(receipt)}
                                          disabled={viewLoadingRow === receiptId}
                                          title="View Details"
                                        >
                                          {viewLoadingRow === receiptId ? (
                                            <ClipLoader size={16} color="#ffffff" />
                                          ) : (
                                            <Eye size={16} />
                                          )}
                                        </Button>
                                        <Button
                                          color="success"
                                          size="sm"
                                          onClick={() => handleExportDetails(receipt)}
                                          disabled={exportingReceiptId === receiptId}
                                          title="Export Details"
                                        >
                                          {exportingReceiptId === receiptId ? (
                                            <ClipLoader size={16} color="#ffffff" />
                                          ) : (
                                            <Download size={16} />
                                          )}
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </Table>
                      </div>

                      {/* Pagination */}
                      {totalCount > pageSize && (
                        <div className="d-flex justify-content-between align-items-center mt-4 p-3"
                          style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6' }}>
                          <div className="text-muted" style={{ fontSize: '14px', fontWeight: '500' }}>
                            <i className="mdi mdi-information-outline me-2" style={{ color: '#556ee6' }}></i>
                            Showing <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{ color: '#556ee6', fontWeight: '600' }}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount}</span> entries
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Button
                              color="light"
                              size="sm"
                              disabled={page === 1}
                              onClick={() => handlePageChange(page - 1)}
                              style={{
                                border: '1px solid #dee2e6',
                                borderRadius: '0.375rem',
                                minWidth: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                                color: page === 1 ? '#6c757d' : '#495057'
                              }}
                            >
                              <i className="mdi mdi-chevron-left"></i>
                            </Button>

                            {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                              const pageNum = index + 1;
                              const isActive = page === pageNum;

                              return (
                                <Button
                                  key={pageNum}
                                  color={isActive ? "primary" : "light"}
                                  size="sm"
                                  onClick={() => handlePageChange(pageNum)}
                                  style={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '0.375rem',
                                    minWidth: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: isActive ? '#556ee6' : '#fff',
                                    borderColor: isActive ? '#556ee6' : '#dee2e6',
                                    color: isActive ? '#fff' : '#495057',
                                    fontWeight: isActive ? '600' : '400',
                                    boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                  }}
                                >
                                  {pageNum}
                                </Button>
                              );
                            })}

                            <Button
                              color="light"
                              size="sm"
                              disabled={page >= Math.ceil(totalCount / pageSize)}
                              onClick={() => handlePageChange(page + 1)}
                              style={{
                                border: '1px solid #dee2e6',
                                borderRadius: '0.375rem',
                                minWidth: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: page >= Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                                color: page >= Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                              }}
                            >
                              <i className="mdi mdi-chevron-right"></i>
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Receipt Details Modal */}
        <Modal isOpen={showReceiptModal} toggle={() => setShowReceiptModal(false)} size="lg">
          <ModalHeader toggle={() => setShowReceiptModal(false)}>
            Receipt Details
          </ModalHeader>
          <ModalBody>
            {loadingReceiptDetails ? (
              <div className="text-center py-4">
                <ClipLoader color="#556ee6" loading={true} size={50} />
                <p className="mt-3">Loading receipt details...</p>
              </div>
            ) : receiptDetails ? (
              <div>
                {/* Receipt Header Information */}
                <Card className="mb-3 border-primary">
                  <CardBody>
                    <h6 className="card-title text-primary mb-3">Receipt Information</h6>
                    <Row>
                      <Col md={6}>
                        <p className="mb-2">
                          <strong>Receipt Number:</strong> {receiptDetails.receiptNumber || receiptDetails.ReceiptNumber || 'N/A'}
                        </p>
                        <p className="mb-2">
                          <strong>Date:</strong> {formatDate(receiptDetails.receiptDate || receiptDetails.ReceiptDate)}
                        </p>
                        <p className="mb-2">
                          <strong>Customer:</strong> {receiptDetails.customer_NameEn || receiptDetails.Customer_NameEn || 'N/A'}
                        </p>
                        <p className="mb-2">
                          <strong>Company:</strong> {receiptDetails.business_NameEn || receiptDetails.Business_NameEn || 'N/A'}
                        </p>
                      </Col>
                      <Col md={6}>
                        <p className="mb-2">
                          <strong>Branch:</strong> {receiptDetails.branch_NameEn || receiptDetails.Branch_NameEn || 'N/A'}
                        </p>
                        <p className="mb-2">
                          <strong>Total Amount:</strong> <span className="text-success fw-bold">{formatCurrency(receiptDetails.amount || receiptDetails.Amount || 0)}</span>
                        </p>
                        <p className="mb-2">
                          <strong>Payment Method:</strong> {
                            (receiptDetails.paymentType || receiptDetails.PaymentType || 'ca') === 'ca' ? 'Cash' :
                              (receiptDetails.paymentType || receiptDetails.PaymentType) === 'cc' ? 'Credit Card' : 'Online'
                          }
                        </p>
                        {(receiptDetails.authCode || receiptDetails.AuthCode) && (
                          <p className="mb-2">
                            <strong>Auth Code:</strong> {receiptDetails.authCode || receiptDetails.AuthCode}
                          </p>
                        )}
                        <p className="mb-2">
                          <strong>Is Advance:</strong> {
                            (receiptDetails.is_Advance || receiptDetails.Is_Advance) ? (
                              <span className="badge bg-info">Yes</span>
                            ) : (
                              <span className="badge bg-secondary">No</span>
                            )
                          }
                        </p>
                        {(receiptDetails.receipt_DocumentPath || receiptDetails.Receipt_DocumentPath) && (
                          <p className="mb-2">
                            <strong>Receipt Document:</strong>
                            <br />
                            <a
                              href={`${API_URL}${receiptDetails.receipt_DocumentPath || receiptDetails.Receipt_DocumentPath}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary mt-1"
                            >
                              <FileText size={16} className="me-1" />
                              View Document
                            </a>
                          </p>
                        )}
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* Payment Allocation Details */}
                {(() => {
                  const isAdvance = receiptDetails.is_Advance || receiptDetails.Is_Advance || false;
                  const hasDetails = receiptDetails.receiptDetails && receiptDetails.receiptDetails.length > 0;

                  if (hasDetails) {
                    return (
                      <Card className="mb-3 border-warning">
                        <CardBody>
                          <h6 className="card-title text-warning mb-3">Payment Allocation</h6>
                          <div className="table-responsive">
                            <Table bordered size="sm">
                              <thead className="table-light">
                                <tr>
                                  <th>Invoice No</th>
                                  <th>Invoice Date</th>
                                  <th>Invoice Amount</th>
                                  <th>Outstanding Before</th>
                                  <th>Paid Amount</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {receiptDetails.receiptDetails.map((detail, index) => {
                                  console.log(`ReceiptDetail ${index}:`, detail);

                                  const invoiceAmount = detail.invoiceAmount || detail.InvoiceAmount || 0;
                                  const paidAmount = detail.paidAmount || detail.PaidAmount || 0;
                                  const totalAmount = detail.totalAmount || detail.TotalAmount || 0;
                                  const isFullyPaid = paidAmount >= totalAmount;

                                  // Try multiple property name variations for invoice number
                                  const invoiceNo = detail.invoice_No || detail.Invoice_No || detail.invoiceNo || detail.InvoiceNo ||
                                    detail.invoiceNumber || detail.InvoiceNumber || null;

                                  // Try multiple property name variations for invoice date
                                  const invoiceDate = detail.invoice_Date || detail.Invoice_Date || detail.invoiceDate ||
                                    detail.InvoiceDate || null;

                                  // If invoice number is still null, try to get it from InvoiceId if we have a mapping
                                  // For now, show InvoiceId if no invoice number
                                  const displayInvoiceNo = invoiceNo || (detail.invoiceId || detail.InvoiceId ? `Invoice #${detail.invoiceId || detail.InvoiceId}` : 'N/A');

                                  return (
                                    <tr key={index}>
                                      <td>{displayInvoiceNo}</td>
                                      <td>
                                        {invoiceDate ? formatDate(invoiceDate) : 'N/A'}
                                      </td>
                                      <td>{formatCurrency(invoiceAmount)}</td>
                                      <td>{formatCurrency(totalAmount)}</td>
                                      <td className="fw-bold text-success">{formatCurrency(paidAmount)}</td>
                                      <td>
                                        {isFullyPaid ? (
                                          <span className="badge bg-success">Fully Paid</span>
                                        ) : (
                                          <span className="badge bg-warning text-dark">Partially Paid</span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot className="table-light">
                                <tr>
                                  <th colSpan="4" className="text-end">Total Paid:</th>
                                  <th className="text-success">
                                    {formatCurrency(
                                      receiptDetails.receiptDetails.reduce(
                                        (sum, d) => sum + (d.paidAmount || d.PaidAmount || 0),
                                        0
                                      )
                                    )}
                                  </th>
                                  <th></th>
                                </tr>
                              </tfoot>
                            </Table>
                          </div>
                        </CardBody>
                      </Card>
                    );
                  } else if (isAdvance) {
                    return (
                      <Card className="mb-3 border-info">
                        <CardBody>
                          <div className="alert alert-info mb-0">
                            <i className="fas fa-info-circle me-2"></i>
                            This is an advance payment. No invoices were allocated to this receipt.
                          </div>
                        </CardBody>
                      </Card>
                    );
                  } else {
                    return (
                      <Card className="mb-3 border-warning">
                        <CardBody>
                          <div className="alert alert-warning mb-0">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            No payment allocation details available for this receipt.
                          </div>
                        </CardBody>
                      </Card>
                    );
                  }
                })()}

                {/* Summary Section */}
                <Card className="border-success">
                  <CardBody>
                    <h6 className="card-title text-success mb-3">Summary</h6>
                    <Row>
                      <Col md={6}>
                        <p className="mb-2">
                          <strong>Total Receipt Amount:</strong> <span className="text-success fw-bold">{formatCurrency(receiptDetails.amount || receiptDetails.Amount || 0)}</span>
                        </p>
                        {receiptDetails.receiptDetails && receiptDetails.receiptDetails.length > 0 && (
                          <p className="mb-2">
                            <strong>Invoices Paid:</strong> {receiptDetails.receiptDetails.length}
                          </p>
                        )}
                      </Col>
                      <Col md={6}>
                        {receiptDetails.receiptDetails && receiptDetails.receiptDetails.length > 0 && (
                          <p className="mb-2">
                            <strong>Total Allocated:</strong> <span className="text-primary fw-bold">
                              {formatCurrency(
                                receiptDetails.receiptDetails.reduce(
                                  (sum, d) => sum + (d.paidAmount || d.PaidAmount || 0),
                                  0
                                )
                              )}
                            </span>
                          </p>
                        )}
                        {(receiptDetails.is_Advance || receiptDetails.Is_Advance) && (
                          <p className="mb-2">
                            <strong>Advance Amount:</strong> <span className="text-info fw-bold">{formatCurrency(receiptDetails.amount || receiptDetails.Amount || 0)}</span>
                          </p>
                        )}
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-muted">No receipt details available</p>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowReceiptModal(false)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>

      </div>
    </React.Fragment>
  );
}

